import { Injectable } from '@angular/core';
import { User } from '../interfaces/auth/users';
import { BehaviorSubject } from 'rxjs';
import { share } from 'rxjs/operators';



export interface RequiredUserRoles {
  combineMode: 'all' | 'any';
  roles: string[];
}



export interface RequiredUserPermissions {
  combineMode: 'all' | 'any';
  permissions: string[];
}



@Injectable({
  providedIn: 'root',
})
export class UserStore {
  private user: User | null = null;

  protected userState$ = new BehaviorSubject<User | null>(null);

  getUser(): User | null {
    return this.user;
  }

  setUser(paramUser: User) {
    this.user = {
      ...paramUser,
      name: `${paramUser.firstName} ${paramUser.lastName}`
    };

    this.changeUserState(paramUser);
  }

  onUserStateChange() {
    return this.userState$.pipe(share());
  }

  changeUserState(paramUser: User | null) {
    this.userState$.next(paramUser);
  }

  hasRole(role: string) {
    return this.roles.has((role ?? '').normalize().toLowerCase().trim());
  }

  hasAnyRole(roles: string[]): boolean {
    return roles.some(role => this.hasRole(role));
  }

  hasAllRoles(roles: string[]): boolean {
    return roles.every(role => this.hasRole(role));
  }

  hasPermission(permission: string): boolean {
    return this.permissions.has(permission) ?? false;
  }

  hasAnyPermission(permissions: string[]): boolean {
    return permissions.some(permission => this.hasPermission(permission));
  }

  hasAllPermissions(permissions: string[]): boolean {
    return permissions.every(permission => this.hasPermission(permission));
  }

  /**
   * Determines whether or not the current user is authorized to access 
   * a resource given the list of required user roles and/or permissions.
   * @param requiredRoles the list of user roles that are required to access
   * the given resource as well as the combination mode to use for the list of
   * user roles that is supplied (i.e., all/any of the given user roles are/is 
   * required in order to access the resource)
   * @param requiredPerms the list of permissions that are required to access
   * the given resource as well as the combination mode to use for the list of
   * permissions that is supplied (i.e., all/any of the given permissions are/is 
   * required in order to access the resource)
   * @param combineMode determines whether **either user roles or permissions**
   * or **both user roles and permissions** are required in order to access the
   * resource.
   * @returns
   * true if the user is authorized to access the resource, false otherwise
   */
  isAuthorizedFor(requiredRoles: RequiredUserRoles | null, requiredPerms: RequiredUserPermissions | null, combineMode: 'either' | 'both'): boolean {
    return combineMode === 'either' ? (
      (requiredPerms ? (
        requiredPerms.combineMode === 'all' ? this.hasAllPermissions(requiredPerms.permissions) : this.hasAnyPermission(requiredPerms.permissions)
      ) : true) || (requiredRoles ? (
        requiredRoles.combineMode === 'all' ? this.hasAllRoles(requiredRoles.roles) : this.hasAnyRole(requiredRoles.roles)
      ) : true)
    ) : (
      (requiredPerms ? (
        requiredPerms.combineMode === 'all' ? this.hasAllPermissions(requiredPerms.permissions) : this.hasAnyPermission(requiredPerms.permissions)
      ) : true) && (requiredRoles ? (
        requiredRoles.combineMode === 'all' ? this.hasAllRoles(requiredRoles.roles) : this.hasAnyRole(requiredRoles.roles)
      ) : true)
    );
  }

  private get permissions(): Set<string> {
    return new Set(this.user?.permissions ?? []);
  }

  private get roles(): Set<string> {
    return new Set([this.user?.role?.name?.normalize()?.toLowerCase()?.trim() ?? '']);
  }

  setSetting(themeName: string) {
    if (this.user) {
      if (this.user.settings) {
        this.user.settings.themeName = themeName;
      } else {
        this.user.settings = { themeName: themeName };
      }

      this.changeUserState(this.user);
    }
  }

  clear(): void {
    this.user = null;
    this.changeUserState(null);
  }
}
